<template>
  <div v-if="!pending">
    <div :class="$style.question_session">
      <div :class="$style.heading_area">
        <MoleculesConsumerHeading :class="$style.heading" title="よくある質問・ご利用ガイド" align="left" />
      </div>
      <div :class="$style.questions">
        <a
          v-for="category in helpCategoryRef"
          :key="category.document_id"
          v-scroll-to="`#about-${category.document_id}`"
          :class="$style.question"
        >
          <div :class="$style.arrow_right_icon">
            <AtomsConsumerImage :src="'/images/site/interview/arrow-right.svg'" alt="右矢印" />
          </div>
          <div :class="$style.link_wrapper">
            <div v-if="category.icon_img_url || getIconPathByCategoryId(category.document_id)" :class="$style.icon">
              <AtomsConsumerImage
                :src="
                  category.icon_img_url
                    ? category.icon_img_url
                    : `/images/site/support/${getIconPathByCategoryId(category.document_id)}.svg`
                "
                :alt="category.name + 'のアイコン'"
              />
            </div>
            <div :class="$style.content">
              <h4 :class="$style.title">{{ category.name }}</h4>
              <p :class="$style.summary">{{ category.description }}</p>
            </div>
          </div>
        </a>
      </div>
    </div>
    <div :class="$style.about_contents">
      <div v-for="category in helpCategoryRef" :key="category.document_id" :class="$style.about_content">
        <h2 :id="`about-${category.document_id}`" :class="$style.about">
          {{ category.name }}
        </h2>
        <MoleculesConsumerAccordion
          v-for="(help, index) in category.helps"
          :id="`accordion-${help.document_id}`"
          :key="help.document_id"
          :class="[$style.accordion, { [$style.first_accordion]: index === 0 }]"
          :is-open-by-default="isOpenByDefaultAccordionId === help.document_id"
          icon="arrow"
        >
          <template #title>
            <h4 :class="$style.title">{{ help.title }}</h4>
          </template>
          <template #content>
            <div :class="$style.answer">
              <div :class="$style.txt" v-html="sanitizeHtmlContent(help.content)" />
            </div>
          </template>
        </MoleculesConsumerAccordion>
      </div>
    </div>

    <div :class="$style.contact_area">
      <div :class="$style.container">
        <h2 :class="$style.contact_header">よくあるご質問に該当がなかった場合は、下記の窓口へお問い合わせください。</h2>
        <div :class="$style.button_area">
          <!-- gtm_mail_contactセレクタはグーグルタグマネージャーで使用 -->
          <AtomsConsumerLink :url="`/contact`" :class="$style.link" class="gtm_mail_contact">
            <MoleculesConsumerButtonWithArrowRight variant="secondary" :class="$style.button">
              個人のお客様はこちら
            </MoleculesConsumerButtonWithArrowRight>
          </AtomsConsumerLink>
          <AtomsConsumerLink url="https://corp.tential.jp/contact" :class="$style.link">
            <MoleculesConsumerButtonWithArrowRight variant="secondary" :class="$style.button">
              法人のお客様はこちら
            </MoleculesConsumerButtonWithArrowRight>
          </AtomsConsumerLink>
        </div>
      </div>
    </div>
    <!-- まだ完全に移行できてないので一旦置くだけ -->
    <div :class="$style.browsing_history">
      <MoleculesConsumerHeading :class="$style.head_text" title="閲覧した商品" variant="sub" align="left" />

      <ClientOnly>
        <PagePartsProductBrowsingHistory />
      </ClientOnly>
    </div>
  </div>
  <OrganismsLoading v-else />
</template>

<script setup lang="ts">
import { FAQPage, WithContext } from "schema-dts"
import { sanitizeHtmlContent } from "@tential/ec-gql-schema/utils/functions/sanitize"
import VueScrollTo from "vue-scrollto"
import { useToast } from "~/state"
import { useCustomMeta } from "~/composables/meta"
import { useHelpCategoryAllWithHelpsQuery } from "~/gql/urql.generated"

// Initialization
const customMeta = useCustomMeta()
const { SITE_SLUG } = useSite()
const siteSlug = SITE_SLUG
const toast = useToast()
const route = useRoute()
const isOpenByDefaultAccordionId = ref("")

isOpenByDefaultAccordionId.value = typeof route.query?.accordionId === "string" ? route.query?.accordionId : ""

// Todo: 後ほど管理画面から入稿できるようにする
const categoryIcons = [
  { categoryId: "65680a0921ad3b006f53f955", path: "info-waves" },
  { categoryId: "63e490af4661c9f8d47311f2", path: "icon-park-outline" },
  { categoryId: "63e490c24661c9f8d47311fc", path: "truck" },
  { categoryId: "63e490e94661c9f8d4731206", path: "user-profile-focus" },
  { categoryId: "63e490fe4661c9f8d4731210", path: "product-exchange" },
  { categoryId: "63e491114661c9f8d4731218", path: "gift" },
] as const
const getIconPathByCategoryId = (categoryId: string): string | undefined => {
  const icon = categoryIcons.find((icon) => icon.categoryId === categoryId)
  return icon?.path
}

// Query for the FAQ
const { executeQuery } = await useHelpCategoryAllWithHelpsQuery({
  variables: { filter: { is_status: true }, sort: "DISPLAY_ORDER_NUMBER_ASC" },
  requestPolicy: "cache-first",
  pause: true,
})

const { data: helpCategoryRef, pending } = await useAsyncData(async () => {
  const { data: queryResult, error } = await executeQuery()
  if (error.value) {
    console.warn(queryResult.value?.helpCategoryPagination, "queryResult")
    toast?.showErrorToasted("カテゴリを取得できませんでした")
    throw createError({ statusCode: 500, statusMessage: "Page Not Found support" })
  }
  return queryResult.value?.helpCategoryPagination?.items ?? []
})

onMounted(() => {
  if (route.query?.accordionId) {
    VueScrollTo.scrollTo("#accordion-" + route.query.accordionId, 0, {
      offset: 10,
    })
  }
})

const jsonLd: WithContext<FAQPage> = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity:
    helpCategoryRef.value
      ?.map((item) => {
        return item.helps
          .map((faq) => {
            return {
              "@type": "Question" as const,
              name: faq.title,
              acceptedAnswer: {
                "@type": "Answer" as const,
                text: faq.content,
              },
            }
          })
          .flat()
      })
      .flat() || [],
}

// Meta tags
useHead(
  customMeta.fetch({
    title: "よくある質問・ご利用ガイド",
    description: `${siteSlug}のよくある質問・ご利用ガイドページです。${siteSlug}の商品をより良く使って頂くため、商品を熟知したスタッフが、皆様のご質問にお答え致します。お気軽に、ご連絡ください。`,
    jsonLDInfo: jsonLd,
  }),
)
</script>

<style lang="scss" module>
.question_session {
  @include container-lg;
  margin-bottom: 4rem;
  @include md {
    margin-bottom: 6rem;
  }
  .heading_area {
    padding: 0 1.25rem;
    @include md {
      padding: 0;
    }
  }
  .questions {
    padding: 0 1.25rem;
    display: grid;
    gap: 15px;
    grid-template-columns: repeat(1, 1fr);
    @include md {
      grid-template-columns: repeat(3, 1fr);
      padding: 0;
      gap: 30px;
    }
    .question {
      display: flex;
      flex-direction: column;
      padding: 15px 15px 30px 30px;
      border-radius: 6px;
      border: 1px solid $secondary-20;
      background-color: $secondary-10;
      cursor: pointer;
      .arrow_right_icon {
        display: flex;
        justify-content: flex-end;
      }
      .link_wrapper {
        display: flex;
        align-items: center;
        gap: 19px;
        .icon {
          img {
            max-width: 38px;
            max-height: 38px;
          }
        }
        .content {
          display: flex;
          flex-direction: column;
          gap: 3px;
          @include md {
            gap: 6px;
          }
          .title {
            font-size: $font-size-15;
            line-height: 1.6;
            color: $primary-100;
            @include md {
              font-size: $font-size-18;
              line-height: 1.33;
            }
          }
          .summary {
            font-size: $font-size-12;
            line-height: 1.66;
            @include md {
              font-size: $font-size-13;
              line-height: 1.53;
            }
          }
        }
      }
    }
  }
}
.about_contents {
  text-align: left;
  @include container-lg;
  margin-bottom: 4rem;
  @include md {
    margin-bottom: 6rem;
  }
  img {
    max-width: 100%;
  }
  .about_content {
    &:not(:first-child) {
      margin-top: 50px;
      @include md {
        margin-top: 70px;
      }
    }

    .about {
      margin-bottom: 1.25rem;
      font-size: $font-size-20;
      font-weight: bold;
      line-height: 1.6;
      letter-spacing: 0.4px;
      color: $primary-100;
      @include md {
        margin-bottom: 2.5rem;
        font-size: $font-size-26;
        line-height: 1.38;
        letter-spacing: inherit;
      }
    }
    .accordion {
      border-bottom: 1px solid $primary-20;
      font-size: 0.875rem;
      line-height: 2;
      padding: 1.25rem 0;
      cursor: pointer;
      font-weight: 700;
      margin: 0;
      text-align: left;
      @include md {
        padding: 30px 0;
      }
      &.first_accordion {
        border-top: 1px solid $primary-20;
      }
      .title {
        margin: 0;
        line-height: 2;
        font-size: $font-size-14;
        font-weight: normal;
        line-height: 1.46;
        text-align: left;
        cursor: pointer;

        @include md {
          font-size: $font-size-15;
          line-height: 2;
        }
      }
      .answer {
        padding: 1.5rem 0;
        .txt {
          font-size: $font-size-14;
          font-weight: normal;
          :global {
            p {
              font-size: $font-size-14;
              line-height: 2;
              letter-spacing: 1.6px;
              @include md {
                font-size: $font-size-15;
                line-height: 1.86;
              }
            }

            .red {
              font-weight: bold;
              color: $danger;
            }

            a {
              @include link-style;
            }
          }
        }
      }
    }
  }
}
.contact_area {
  @include container-lg;
  margin-bottom: 4rem;
  @include md {
    margin-bottom: 6rem;
  }
  .container {
    padding: 30px;
    background-color: $primary-10;
    @include md {
      padding: 40px 30px 50px;
    }
    .contact_header {
      font-size: $font-size-13;
      line-height: 1.84;
      letter-spacing: -0.52px;
      margin-bottom: 15px;
      font-weight: bold;
      text-align: center;
      color: $primary-100;
      @include md {
        font-size: $font-size-18;
        line-height: 2;
        letter-spacing: inherit;
        margin-bottom: 20px;
      }
    }
    .button_area {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      @include md {
        flex-direction: row;
        justify-content: center;
        gap: 30px;
      }
      .link {
        display: inline-block;
        width: 100%;
        @include md {
          max-width: 344px;
        }
        .button {
          justify-content: center;
          width: 100%;
          font-size: $font-size-12;
        }
      }
    }
  }
}
.browsing_history {
  @include container-lg;
  margin-bottom: 4rem;
  @include md {
    margin-bottom: 6rem;
  }
  .head_text {
    margin-bottom: 30px;
    @include md {
      margin-bottom: 50px;
    }
  }
}
</style>
